import type { ChatClient, ChatPerson, ChatRoomSummary } from '@wtfalch/chat';
import { Button, Callout, Icon, IdentityStack, Panes, Pill, Typing } from '@wtfalch/design';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCallSession } from '../calls/CallSession.tsx';
import { CallSurface } from '../calls/CallSurface.tsx';
import { callNoticeBody, parseCallNotice } from '../calls/callNotice.ts';
import type { RoomCallBinding } from '../calls/roomCalls.ts';
import type { ChatLocation } from '../routes.ts';
import { Avatar } from '../ui/Avatar.tsx';
import { ENCRYPTED_ROOM_NOTICE } from './encryption.ts';
import { shortName } from './format.ts';
import { AvatarWithPresence, directPeerId, StatusBanner, StatusLabel } from './presence.tsx';
import { RoomComposer } from './RoomComposer.tsx';
import { RoomTopic } from './RoomTopic.tsx';
import { Stream } from './Stream.tsx';
import { useTypingNames } from './typing.ts';
import { useTimeline } from './useTimeline.ts';

/** A pin Synapse refuses (#255): a room whose power levels predate the
 *  change that lets an ordinary member pin at all. `pinMessage`/
 *  `unpinMessage` reject rather than resolving quietly, on purpose, so this
 *  is what turns that rejection into something a person actually sees. */
const PIN_FAILED_MESSAGE =
  "Couldn't update the pin. This room may be too old for pinning to work in it yet.";

/**
 * A room's own pane: its head, its stream and its composer -- matching the
 * canvas's `chat()`/`dm()`. One component for a channel and a direct
 * message, the same way the board draws the same shape for both; only the
 * head's own icon and what it says beside the name differ.
 *
 * The head also carries what `ChatClient` now backs: a mute bell (#253), a
 * pin count that opens the third column's Pinned tab (#255), and, for a
 * direct message, the other side's presence dot (#254) beside their name.
 * No "send as mail," no "offer a time" -- neither is a `ChatClient` call,
 * and this draws neither rather than a button that would do nothing. See the
 * campaign ledger's Findings for the rest of that list.
 *
 * A call (agora#251, agora#340) is the one exception, and it is optional on
 * purpose: `calls` is the `CallsClient`/adapter/store bundle
 * `startOrJoinRoomCall` and `CallSurface` need (`../calls/roomCalls.ts`), and
 * nothing in this app constructs one yet -- there is no reverse-proxied,
 * same-origin `@wtfalch/calls` deployment for this agora, and no production
 * `CallEndpointStore` for its E2EE key material (`wtfalch/calls`'s own one
 * is a "proof," explicitly not an XSS boundary). Until a host supplies
 * `calls`, the room draws no call button at all, the same reasoning this
 * docblock already gives for mail and scheduling.
 */
export function Room({
  client,
  room,
  onBack,
  onOpenThread,
  infoOpen,
  onToggleInfo,
  onOpenPinned,
  calls,
  linkFor,
  encrypted = false,
}: {
  client: ChatClient;
  room: ChatRoomSummary;
  onBack: () => void;
  onOpenThread: (rootId: string) => void;
  infoOpen: boolean;
  onToggleInfo: () => void;
  onOpenPinned: () => void;
  calls?: RoomCallBinding;
  /** The address of a place in a room, for a message's "Copy link". */
  linkFor?: (location: ChatLocation) => string;
  /** Whether this room is end-to-end encrypted (agora#135). */
  encrypted?: boolean;
}) {
  const [members, setMembers] = useState<ChatPerson[]>(() => client.roomMembers(room.id));
  const [pinnedIds, setPinnedIds] = useState<ReadonlySet<string>>(
    () => new Set(client.pinnedMessages(room.id).map((event) => event.id)),
  );
  const [pinError, setPinError] = useState<string | null>(null);

  useEffect(() => {
    function read() {
      setMembers(client.roomMembers(room.id));
      setPinnedIds(new Set(client.pinnedMessages(room.id).map((event) => event.id)));
    }
    read();
    setPinError(null);
    return client.onTimelineChanged(room.id, read);
  }, [client, room.id]);

  const [callLive, setCallLive] = useState(false);
  const [callError, setCallError] = useState<string | null>(null);

  // `Room` is reused across room switches rather than remounted (the pin/
  // member reads above key off `room.id` the same way), so anything async
  // that started against one room has to recognise landing in another one
  // instead of touching that room's state. Both refs are updated on every
  // render rather than in an effect, so a read of `.current` is never a
  // render behind the value a callback closed over.
  const roomIdRef = useRef(room.id);
  roomIdRef.current = room.id;
  const callsRef = useRef(calls);
  callsRef.current = calls;

  // The call itself lives above the routes (`../calls/CallSession.tsx`), so
  // leaving this room never ends it; this room only shows it as joined.
  const session = useCallSession();
  const onThisCall = session?.call?.roomId === room.id;
  const onOtherCall = Boolean(session?.call) && !onThisCall;
  const callEnded = session?.ended?.target.roomId === room.id && !session.call;
  const callFocused = (onThisCall && Boolean(session?.focused)) || callEnded;
  const callStarting = session?.starting ?? false;

  // A room re-opened while someone else's call is already running there
  // should offer "Join call," not "Start call" -- `roomStatus` is the one
  // read that tells the two apart. Depends on `room.id` and whether `calls`
  // is present at all, never on `calls`'s own identity (read through
  // `callsRef`): a host that hands down a fresh `calls` object on every
  // render never re-fires this and never drops a call already in progress,
  // but a host that only has `calls` ready a render or two after the first
  // one still gets this read once it arrives, rather than being stuck
  // offering "Start call" for a room someone is already on.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above -- `calls` is read through `callsRef`; only its presence, not its identity, belongs in this list.
  useEffect(() => {
    setCallError(null);
    setCallLive(false);
    const calls = callsRef.current;
    if (!calls) return;
    let current = true;
    calls.client.roomStatus(room.id).then(
      (status) => {
        if (current) setCallLive(status.live);
      },
      () => {
        // A failed read just means the button defaults to "Start call" --
        // the click itself re-checks and surfaces a real error if the
        // service is actually unreachable.
      },
    );
    return () => {
      current = false;
    };
  }, [room.id, Boolean(calls)]);

  const startCall = useCallback(() => {
    const calls = callsRef.current;
    if (!calls || !session || onThisCall || onOtherCall) return;
    const roomId = room.id;
    setCallError(null);
    void session
      .start(
        { roomId, title: room.name, path: window.location.pathname, direct: room.isDirect },
        calls,
      )
      .then((result) => {
        if (!result.ok) {
          if (roomIdRef.current === roomId) setCallError(result.error);
          return;
        }
        // The starter tells the room the call was created in, even if the
        // person has since switched away; receivers still check with the
        // calls service before they ring.
        if (result.created) {
          void Promise.resolve()
            .then(() => client.sendMessage(roomId, callNoticeBody(result.sessionId)))
            .catch((error: unknown) => console.error('Could not post the call notice:', error));
        }
      });
  }, [session, onThisCall, onOtherCall, room.id, room.name, room.isDirect, client]);

  const togglePin = useCallback(
    (eventId: string) => {
      const pin = !pinnedIds.has(eventId);
      const action = pin
        ? client.pinMessage(room.id, eventId)
        : client.unpinMessage(room.id, eventId);
      action.then(
        () => setPinError(null),
        () => setPinError(PIN_FAILED_MESSAGE),
      );
    },
    [client, room.id, pinnedIds],
  );

  const timeline = useTimeline(client, room.id, null);
  // The latest call notice somebody else posted: it names who started the
  // call, and a new one is the cue to ask the calls service again, so the
  // banner appears without the room being re-opened. The notice alone never
  // shows the banner: anyone can type one, only `roomStatus` says it is live.
  const startedBy = timeline.page.events
    .filter((e) => !e.mine && parseCallNotice(e.body) !== null)
    .at(-1);
  const startedById = startedBy?.id;
  useEffect(() => {
    const calls = callsRef.current;
    if (!calls || !startedById) return;
    let current = true;
    calls.client.roomStatus(room.id).then(
      (status) => {
        if (current) setCallLive(status.live);
      },
      (error: unknown) => {
        console.error('Could not read the call status:', error);
        if (current) setCallError('Could not check whether the call is still going.');
      },
    );
    return () => {
      current = false;
    };
  }, [startedById, room.id]);

  const typingNames = useTypingNames(client, room.id);
  const peerId = directPeerId(client, room);

  return (
    <Panes.Column flex className="chat-room">
      <div className="flex items-center gap-3 chat-pane-head">
        <Button
          kind="ghost"
          size="sm"
          iconOnly
          className="chat-back"
          aria-label="Back to rooms"
          onPress={onBack}
        >
          <Icon name="back" size={18} />
        </Button>
        {room.isDirect ? (
          peerId ? (
            <AvatarWithPresence client={client} userId={peerId} name={room.name} seed={room.id} />
          ) : (
            <Avatar name={room.name} seed={room.id} />
          )
        ) : (
          <span className="flex flex-none items-center justify-center w-5.5 text-muted roomrow-icon">
            <Icon name="hash" size={20} />
          </span>
        )}
        <span className="flex flex-col grow min-w-0" style={{ gap: 2 }}>
          <span className="flex items-center gap-2 min-w-0">
            <span className="h3 truncate">{room.name}</span>
            {encrypted && (
              <Pill tone="good" quiet>
                <Icon name="lock" size={12} /> Encrypted
              </Pill>
            )}
          </span>
          {room.isDirect ? (
            <span className="flex items-center gap-2 text-xs text-muted">
              Direct message
              <StatusLabel client={client} userId={peerId} />
            </span>
          ) : (
            <span className="flex items-center gap-2 text-xs text-muted chat-member-stack">
              <IdentityStack
                people={members.map((m) => ({ name: shortName(m.name), address: m.userId }))}
                max={3}
                size="sm"
              />
              {members.length} member{members.length === 1 ? '' : 's'}
            </span>
          )}
          {!room.isDirect && <RoomTopic client={client} room={room} />}
        </span>
        <span className="flex items-center gap-2 flex-none chat-pane-actions">
          {calls && (
            <Button
              kind="ghost"
              size="sm"
              iconOnly
              aria-label={
                onOtherCall
                  ? 'Leave the call you are on first'
                  : onThisCall
                    ? 'In this call'
                    : callLive
                      ? 'Join call'
                      : 'Start call'
              }
              disabled={callStarting || onThisCall || onOtherCall}
              onPress={startCall}
            >
              <Icon name="phone" size={18} />
            </Button>
          )}
          <Button
            kind="ghost"
            size="sm"
            iconOnly
            aria-label={room.muted ? `Unmute ${room.name}` : `Mute ${room.name}`}
            aria-pressed={room.muted}
            onPress={() => void client.muteRoom(room.id, !room.muted)}
          >
            <Icon name={room.muted ? 'bell-off' : 'bell'} size={18} />
          </Button>
          <Button
            kind="ghost"
            size="sm"
            iconOnly
            className={pinnedIds.size > 0 ? 'chat-pin-btn' : undefined}
            aria-label={
              pinnedIds.size > 0
                ? `${pinnedIds.size} pinned message${pinnedIds.size === 1 ? '' : 's'}`
                : 'Pinned messages'
            }
            onPress={onOpenPinned}
          >
            <Icon name="menu" size={18} />
            {pinnedIds.size > 0 && <span className="unread text-sm">{pinnedIds.size}</span>}
          </Button>
          <Button
            kind="ghost"
            size="sm"
            iconOnly
            aria-label="Room details"
            aria-pressed={infoOpen}
            onPress={onToggleInfo}
          >
            <Icon name="people" size={18} />
          </Button>
        </span>
      </div>
      {pinError && (
        <Callout tone="bad" timed onDismiss={() => setPinError(null)}>
          {pinError}
        </Callout>
      )}
      {encrypted && <Callout tone="info">{ENCRYPTED_ROOM_NOTICE}</Callout>}
      {callError && (
        <Callout tone="bad" timed onDismiss={() => setCallError(null)}>
          {callError}
        </Callout>
      )}
      {calls && callLive && !onThisCall && !onOtherCall && (
        <div role="status" className="flex items-center gap-3 flex-none px-5 py-2 chat-call-banner">
          <Icon name="phone" size={16} />
          <span className="text-sm grow min-w-0 truncate">
            {startedBy
              ? `${shortName(startedBy.senderName)} started a call.`
              : 'A call is in progress.'}
          </span>
          <Button size="sm" kind="primary" disabled={callStarting} onPress={startCall}>
            Join
          </Button>
        </div>
      )}
      {calls && (
        <CallSurface
          roomId={room.id}
          className={callFocused ? 'room-call-grid flex-1 min-h-0' : 'room-call-bar'}
        />
      )}
      {!(calls && callFocused) && (
        <>
          <Stream
            client={client}
            roomId={room.id}
            threadId={null}
            timeline={timeline}
            onOpenThread={onOpenThread}
            pinnedIds={pinnedIds}
            onTogglePin={togglePin}
            {...(linkFor ? { linkFor } : {})}
            emptyLabel="No messages yet. Say hello."
          />
          <Typing names={typingNames} className="flex-none px-5 chat-typing" />
          {room.isDirect && <StatusBanner client={client} userId={peerId} />}
          <RoomComposer
            key={room.id}
            client={client}
            roomId={room.id}
            threadId={null}
            placeholder={
              onThisCall
                ? 'Message the room…'
                : room.isDirect
                  ? `Message ${shortName(room.name).split(' ')[0]}…`
                  : `Message #${room.name}…`
            }
          />
        </>
      )}
    </Panes.Column>
  );
}
